import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { api } from "@/api";

const SEVERITY_BG = {
  info: "bg-blue-50 border-blue-300 text-blue-900",
  minor: "bg-amber-50 border-amber-300 text-amber-900",
  major: "bg-orange-50 border-orange-400 text-orange-900",
  critical: "bg-red-50 border-red-400 text-red-900",
};

/**
 * Bandeau global affichant les incidents publics actifs (visible sur Landing).
 * Se rafraîchit toutes les 60s.
 */
export function PublicIncidentBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/public/incidents");
      setItems(data || []);
    } catch { setItems([]); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const visible = items.filter((i) => !dismissed[i.id]);
  if (visible.length === 0) return null;

  return (
    <div className="border-b" data-testid="public-incident-banner">
      {visible.map((inc) => (
        <div key={inc.id} className={`border-b ${SEVERITY_BG[inc.severity] || SEVERITY_BG.minor}`}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex items-start gap-3 text-sm">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <span className="font-bold">{inc.title}</span>
              {inc.message && <span className="ml-2 opacity-90">— {inc.message}</span>}
              <Link to="/status" className="ml-2 underline text-xs opacity-80 hover:opacity-100">Détails</Link>
            </div>
            <button
              onClick={() => setDismissed({ ...dismissed, [inc.id]: true })}
              className="opacity-60 hover:opacity-100"
              aria-label="Fermer"
              data-testid={`inc-close-${inc.id}`}
            ><X className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Bandeau intra-portails (chef/salarié/admin), utilise /api/incidents/portal (auth requis).
 */
export function PortalIncidentBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/incidents/portal");
      setItems(data || []);
    } catch { setItems([]); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const visible = items.filter((i) => !dismissed[i.id]);
  if (visible.length === 0) return null;

  return (
    <div data-testid="portal-incident-banner">
      {visible.map((inc) => (
        <div key={inc.id} className={`border-b ${SEVERITY_BG[inc.severity] || SEVERITY_BG.minor}`}>
          <div className="px-6 lg:px-10 py-2.5 flex items-start gap-3 text-xs">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <span className="font-bold">{inc.title}</span>
              {inc.message && <span className="ml-2 opacity-90">— {inc.message}</span>}
              <Link to="/status" className="ml-2 underline text-[10px] opacity-80 hover:opacity-100">Détails</Link>
            </div>
            <button
              onClick={() => setDismissed({ ...dismissed, [inc.id]: true })}
              className="opacity-60 hover:opacity-100"
              aria-label="Fermer"
              data-testid={`portal-inc-close-${inc.id}`}
            ><X className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}
